import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { analyzeVoiceCommand } from '../services/voiceCommandService';
import { useI18n } from '../i18n';

const SpeechContext = createContext(null);

export const useSpeech = () => useContext(SpeechContext);

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

export const safeRecognitionCall = (recognition, method) => {
  if (!recognition || typeof recognition[method] !== 'function') return false;
  try {
    recognition[method]();
    return true;
  } catch (error) {
    console.warn(`Speech: ${method} ignored contextually:`, error.message);
    return false;
  }
};

// Detección de Móvil para ajustar comportamiento de Web Speech API
const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

export const SpeechProvider = ({ children }) => {
  const { language } = useI18n();
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [lastCommand, setLastCommand] = useState(null);

  const recognitionRef = useRef(null);
  const intendedStopRef = useRef(false);
  const isStartingRef = useRef(false); // Bloqueo para evitar doble start
  const isMutedRef = useRef(false);
  const resumeTimeoutRef = useRef(null);
  const listeningRef = useRef(false);
  const shouldResumeAfterSpeechRef = useRef(false);
  const manualStopRef = useRef(true);

  useEffect(() => {
    listeningRef.current = isListening;
  }, [isListening]);

  const clearResumeTimeout = useCallback(() => {
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
  }, []);

  const disposeRecognition = useCallback((recognition, { abort = true } = {}) => {
    if (!recognition) return;
    recognition.onstart = null;
    recognition.onend = null;
    recognition.onerror = null;
    recognition.onresult = null;
    recognition.onaudioend = null;
    recognition.onspeechend = null;
    if (abort) safeRecognitionCall(recognition, 'abort');
  }, []);

  const createRecognition = useCallback(() => {
    if (!SpeechRecognition) {
      console.error("Speech Recognition API is not supported in this browser.");
      return null;
    }

    if (recognitionRef.current) {
      disposeRecognition(recognitionRef.current);
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = !isMobile;
    recognition.interimResults = true;
    recognition.lang = language === 'en' ? 'en-US' : 'es-ES';

    recognition.onstart = () => {
      console.log("Speech: Service Started");
      isStartingRef.current = false;
      setIsListening(true);
    };

    recognition.onend = () => {
      console.log("Speech: Service Ended");
      isStartingRef.current = false;
      setIsListening(false);

      if (!intendedStopRef.current && !isMobile && !isMutedRef.current) {
        console.log("Speech: Restarting via Keep-Alive...");
        try {
          recognitionRef.current?.start();
        } catch (e) {
          console.warn("Speech: Keep-alive restart blocked.", e.message);
          recognitionRef.current = null;
        }
      }
    };

    recognition.onerror = (event) => {
      if (event.error === 'no-speech') return;
      console.error('Speech recognition error:', event.error);
      isStartingRef.current = false;
      setIsListening(false);
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        intendedStopRef.current = true;
        manualStopRef.current = true;
      }
    };

    recognition.onaudioend = () => {
      isStartingRef.current = false;
      setIsListening(false);
    };

    recognition.onspeechend = () => {
      if (isMobile) safeRecognitionCall(recognition, 'stop');
    };

    recognition.onresult = (event) => {
      if (isMutedRef.current) return;
      let finalTranscript = '';
      let interim = '';

      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        } else {
          interim += event.results[i][0].transcript;
        }
      }

      if (finalTranscript) {
         setTranscript(finalTranscript);
         console.log("SpeechContext: Command Finalized ->", finalTranscript);
         setLastCommand({
           text: finalTranscript,
           analysis: analyzeVoiceCommand(finalTranscript, language),
           timestamp: Date.now()
         });
      }

      setInterimTranscript(interim);
    };

    recognitionRef.current = recognition;
    return recognition;
  }, [disposeRecognition, language]);

  useEffect(() => {
    createRecognition();
    return () => {
      intendedStopRef.current = true;
      disposeRecognition(recognitionRef.current);
      recognitionRef.current = null;
      setIsListening(false);
    };
  }, [createRecognition, disposeRecognition]);

  const startListening = useCallback(() => {
    if (isMutedRef.current) return false;
    if (isListening || isStartingRef.current) return false;
    const recognition = recognitionRef.current || createRecognition();
    if (!recognition) return false;
    try {
      isStartingRef.current = true;
      intendedStopRef.current = false;
      manualStopRef.current = false;
      setTranscript('');
      setInterimTranscript('');
      recognition.start();
      return true;
    } catch (error) {
      console.warn("Speech: Start blocked contextually:", error.message);
      isStartingRef.current = false;
      if (error.message?.includes('already started')) {
        setIsListening(true);
        return true;
      }
      recognitionRef.current = null;
      createRecognition();
      setIsListening(false);
      return false;
    }
  }, [createRecognition, isListening]);

  const stopListening = useCallback((options = {}) => {
    const { force = false } = options;
    clearResumeTimeout();
    shouldResumeAfterSpeechRef.current = false;
    manualStopRef.current = true;
    isMutedRef.current = false;
    isStartingRef.current = false;
    intendedStopRef.current = true;
    const recognition = recognitionRef.current;
    if (recognition) {
      if (force) {
        safeRecognitionCall(recognition, 'abort');
        disposeRecognition(recognition, { abort: false });
        recognitionRef.current = null;
        createRecognition();
      } else if (!safeRecognitionCall(recognition, 'stop')) {
        safeRecognitionCall(recognition, 'abort');
      }
    } else if (force) {
      createRecognition();
    }
    setIsListening(false);
    return true;
  }, [clearResumeTimeout, createRecognition, disposeRecognition]);

  const abort = useCallback(() => {
    return stopListening({ force: true });
  }, [stopListening]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  const value = {
    isListening,
    transcript,
    interimTranscript,
    lastCommand,
    toggleListening,
    abort,
    startListening,
    stopListening,
  };

  useEffect(() => {
    const handleSuspend = () => {
      // Limpiamos cualquier reanudación pendiente si Q vuelve a hablar inmediatamente
      clearResumeTimeout();
      shouldResumeAfterSpeechRef.current = shouldResumeAfterSpeechRef.current || (!manualStopRef.current && (listeningRef.current || isStartingRef.current));
      isMutedRef.current = true;
      isStartingRef.current = false;
      intendedStopRef.current = true;
      const recognition = recognitionRef.current;
      if (recognition) {
        safeRecognitionCall(recognition, 'abort');
        disposeRecognition(recognition, { abort: false });
        recognitionRef.current = null;
        createRecognition();
      }
      setIsListening(false);
    };

    const handleResume = () => {
      clearResumeTimeout();
      // Período de gracia de 800ms para dejar que el eco físico de la habitación desaparezca
      resumeTimeoutRef.current = setTimeout(() => {
        resumeTimeoutRef.current = null;
        isMutedRef.current = false;
        if (shouldResumeAfterSpeechRef.current && !manualStopRef.current) {
          shouldResumeAfterSpeechRef.current = false;
          startListening();
          return;
        }
        shouldResumeAfterSpeechRef.current = false;
      }, 800);
    };

    window.addEventListener('q-speech-suspend', handleSuspend);
    window.addEventListener('q-speech-resume', handleResume);
    return () => {
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
      window.removeEventListener('q-speech-suspend', handleSuspend);
      window.removeEventListener('q-speech-resume', handleResume);
    };
  }, [clearResumeTimeout, createRecognition, disposeRecognition, startListening]);

  return <SpeechContext.Provider value={value}>{children}</SpeechContext.Provider>;
};
